// @flow

import React, { type Element, useState } from 'react'
import { Grid, Typography, useTheme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import TermsAndConditions from '../TermsAndConditions'
import FirstNameField from './FirstNameField'
import LastNameField from './LastNameField'
import EmailField from './EmailField'
import PasswordField from './PasswordField'
import ValidationResult from './ValidationResult'
import PasswordConfirmationField from './PasswordConfirmationField'
import FormData from './FormData'

type Props = {
  storeEmail: (string) => void,
  storeRegistrationUuid: (string) => void,
  onServerErrorsCallback: ({ [string]: Array<string> }) => void,
  goNextPageCallback: () => void,
  selectedUserInfo: {| firstName: string, lastName: string, email: string |},
  termsAndConditionsUrls: {| url: string, pdfUrl: string |},
  selectedPlan: {|
    id: number,
    name: string,
    description: string,
    tag: string,
    signUpDescription: string,
    hasExtraSignupTerms: boolean,
    termsAndConditions: string,
    title: Array<string>,
  |},
}

const useStyles = makeStyles({
  terms: {
    fontSize: '14px',
  },
  '@media (max-width: 992px)': {
    fullWidthButton: {
      'flex-grow': '0',
      'max-width': '100%',
      'flex-basis': '100%',
    },
  },
})

function CreateLoginForm({
  storeEmail,
  storeRegistrationUuid,
  onServerErrorsCallback,
  goNextPageCallback,
  selectedUserInfo,
  termsAndConditionsUrls,
  selectedPlan,
}: Props): Element<typeof React.Fragment> {
  const classes = useStyles()

  const [validationResult, setValidationResult] = useState(new ValidationResult())
  const [formData, setFormData] = useState(new FormData())
  const theme = useTheme()

  const handleValidationResultChange = (fieldName: $Keys<ValidationResult>, isValid: boolean) => {
    setValidationResult((prevValue) => prevValue.update(fieldName, isValid))
  }
  const handleFormDataChange = (fieldName: $Keys<FormData>, value: string) => {
    setFormData((prevValue) => prevValue.update(fieldName, value))
  }

  async function handleAcceptCallback(): Promise<boolean> {
    const payload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      password: formData.password,
      plan_id: selectedPlan.tag,
    }

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }

    const path = '/api/v4/sign_up/create_account'

    try {
      const response = await fetch(path, requestOptions)
      const data = await response.json()

      if (response.status === 200) {
        storeEmail(formData.email)
        storeRegistrationUuid(data.registration_uuid)
        return Promise.resolve(true)
      }

      if (response.status === 422) {
        onServerErrorsCallback(data.errors)
      } else {
        onServerErrorsCallback({ non_field_errors: ['Something went wrong.'] })
      }
    } catch {
      onServerErrorsCallback({ non_field_errors: ['Something went wrong.'] })
    }

    return Promise.resolve(false)
  }

  return (
    <>
      <Grid item xs={12}>
        <FirstNameField
          initialValue={selectedUserInfo.firstName}
          value={formData.firstName}
          setIsValid={(isValid) => handleValidationResultChange('firstName', isValid)}
          setValue={(value) => handleFormDataChange('firstName', value)}
        />
      </Grid>
      <Grid item xs={12}>
        <LastNameField
          initialValue={selectedUserInfo.lastName}
          value={formData.lastName}
          setIsValid={(isValid) => handleValidationResultChange('lastName', isValid)}
          setValue={(value) => handleFormDataChange('lastName', value)}
        />
      </Grid>
      <Grid item xs={12}>
        <EmailField
          initialValue={selectedUserInfo.email}
          value={formData.email}
          setIsValid={(isValid) => handleValidationResultChange('email', isValid)}
          setValue={(value) => handleFormDataChange('email', value)}
        />
      </Grid>
      <Grid item xs={12}>
        <PasswordField
          value={formData.password}
          setIsValid={(isValid) => handleValidationResultChange('password', isValid)}
          setValue={(value) => handleFormDataChange('password', value)}
        />
      </Grid>
      <Grid item xs={12}>
        <PasswordConfirmationField
          password={formData.password}
          isPasswordValid={validationResult.password}
          setIsValid={(isValid) => handleValidationResultChange('passwordConfirmation', isValid)}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" className={classes.terms}>
          By entering my email above I agree to receive promotional emails from Dr.Bill. I can
          unsubscribe at any time. For more details, please see our{' '}
          <b>
            <a
              href="https://www.dr-bill.ca/privacy-policy/"
              rel="noopener noreferrer"
              target="_blank"
            >
              Privacy Policy
            </a>{' '}
          </b>
          or&nbsp;
          <b>
            <a href="https://www.dr-bill.ca/contact" rel="noopener noreferrer" target="_blank">
              Contact Us
            </a>
          </b>
          .
        </Typography>
      </Grid>
      <Grid container item style={theme.rightAlignItem}>
        <Grid item className={classes.fullWidthButton}>
          <TermsAndConditions
            isContinueButtonDisabled={!validationResult.isValid()}
            onAcceptCallback={handleAcceptCallback}
            goNextPageCallback={goNextPageCallback}
            iframeUrl={termsAndConditionsUrls.url}
            pdfUrl={termsAndConditionsUrls.pdfUrl}
          />
        </Grid>
      </Grid>
    </>
  )
}

export default CreateLoginForm
